import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Badge } from "./ui/badge";
import { Pagination } from "./ui/pagination";
import { Building2, Plus, Search, Edit, Trash2, Phone, Mail, MapPin, Eye, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supplierApiService, Supplier } from "../services";
import { PermissionGate } from "./ui/PermissionGate";

export function SupplierListingPage() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Client-side pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // Supplier management route (from backend permissions)
  const SUPPLIER_ROUTE = '/suppliers';

  // Load suppliers from API
  useEffect(() => {
    loadSuppliers();
  }, []);

  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const loadSuppliers = async () => {
    try {
      setLoading(true);
      // Fetch all suppliers with a large pageSize to get all data
      const response = await supplierApiService.getSuppliersData(1, 1000);
      setSuppliers(response.suppliers);
    } catch (error) {
      // console.error('Error loading suppliers:', error);
      toast.error('Failed to load suppliers');
      setSuppliers([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter suppliers based on search term
  const filteredSuppliers = suppliers.filter(supplier =>
    supplier.supplierCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    supplier.supplierName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    supplier.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    supplier.contactNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    supplier.stateName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    supplier.gstNo?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination logic
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedSuppliers = filteredSuppliers.slice(startIndex, endIndex);
  const totalPages = Math.ceil(filteredSuppliers.length / pageSize) || 1;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleNewSupplier = () => {
    navigate('/suppliers/information', { state: { mode: 'new' } });
  };

  const handleEditSupplier = (supplier: Supplier) => {
    navigate('/suppliers/information', { state: { mode: 'edit', supplier } });
  };

  const handleViewSupplier = (supplier: Supplier) => {
    navigate('/suppliers/information', { state: { mode: 'view', supplier } });
  };

  const handleDeleteSupplier = async (supplier: Supplier) => {
    if (window.confirm(`Are you sure you want to delete ${supplier.supplierName}?`)) {
      try {
        await supplierApiService.deleteSupplier(supplier.id.toString());
        await loadSuppliers(); // Reload the list
        toast.success('Supplier deleted successfully');
      } catch (error) {
        // console.error('Failed to delete supplier:', error);
        toast.error('Failed to delete supplier');
      }
    }
  };

  const getStatusColor = (isActive: boolean) => {
    if (isActive) {
      return 'bg-green-100 text-green-700 border-green-200';
    } else {
      return 'bg-red-100 text-red-700 border-red-200';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
        <CardHeader className="bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-lg bg-white/20 flex items-center justify-center">
                <Building2 className="h-5 w-5" />
              </div>
              <div>
                <CardTitle className="text-xl">Supplier Management</CardTitle>
                <p className="text-primary-100 text-sm">Manage your supplier information and contacts</p>
              </div>
            </div>
            <PermissionGate route={SUPPLIER_ROUTE} action="create">
              <Button
                onClick={handleNewSupplier}
                className="bg-white text-primary-600 hover:bg-primary-50 shadow-lg"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Supplier
              </Button>
            </PermissionGate>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {/* Search and Filters */}
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search suppliers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-primary-200 focus:border-primary-400 focus:ring-primary-400"
              />
            </div>
            <div className="text-sm text-gray-600">
              {filteredSuppliers.length} of {suppliers.length} suppliers
            </div>
          </div>

          {/* Suppliers Table */}
          <div className="border border-primary-200 rounded-lg overflow-hidden bg-white">
            <Table>
              <TableHeader>
                <TableRow className="bg-primary-50">
                  <TableHead className="font-semibold text-primary-800">Supplier ID</TableHead>
                  <TableHead className="font-semibold text-primary-800 max-w-[200px]">Supplier Name</TableHead>
                  <TableHead className="font-semibold text-primary-800">Contact Info</TableHead>
                  <TableHead className="font-semibold text-primary-800">Location</TableHead>
                  <TableHead className="font-semibold text-primary-800">GST Number</TableHead>
                  <TableHead className="font-semibold text-primary-800">Status</TableHead>
                  <TableHead className="font-semibold text-primary-800">Created Date</TableHead>
                  <TableHead className="font-semibold text-primary-800 text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      <Loader2 className="h-12 w-12 text-primary-300 animate-spin" />
                      <p className="text-gray-500 mt-2">Loading suppliers...</p>
                    </TableCell>
                  </TableRow>
                ) : paginatedSuppliers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                      <Building2 className="h-12 w-12 mx-auto mb-3 text-primary-300" />
                      <p>{searchTerm ? 'No suppliers found matching your search criteria.' : 'No suppliers found.'}</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedSuppliers.map((supplier) => (
                    <TableRow key={supplier.id} className="hover:bg-primary-50/50">
                      <TableCell>
                        <div className="font-medium text-gray-900">
                          {supplier.supplierCode || 'N/A'}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[200px]">
                        <div className="truncate" title={supplier.supplierName || 'N/A'}>
                          <div className="font-semibold text-gray-900 truncate">{supplier.supplierName || 'N/A'}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 text-sm">
                            <Mail className="h-3 w-3 text-gray-400 flex-shrink-0" />
                            <span className="text-gray-600 truncate" title={supplier.email || 'N/A'}>{supplier.email || 'N/A'}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <Phone className="h-3 w-3 text-gray-400 flex-shrink-0" />
                            <span className="text-gray-600">{supplier.contactNo || 'N/A'}</span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-sm">
                          <MapPin className="h-3 w-3 text-gray-400" />
                          <span className="text-gray-600">{supplier.stateName || 'N/A'}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <code className="bg-gray-100 px-2 py-1 rounded text-xs">{supplier.gstNo || 'N/A'}</code>
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(supplier.isActive)}>
                          {supplier.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-gray-600">
                          {supplier.createdAt ? new Date(supplier.createdAt).toLocaleDateString('en-GB') : 'N/A'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-2">
                          <PermissionGate route={SUPPLIER_ROUTE} action="view">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewSupplier(supplier)}
                              className="h-8 w-8 p-0 border-purple-200 text-purple-600 hover:bg-purple-50"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </PermissionGate>
                          <PermissionGate route={SUPPLIER_ROUTE} action="edit">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditSupplier(supplier)}
                              className="h-8 w-8 p-0 border-purple-200 text-purple-600 hover:bg-purple-50"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </PermissionGate>
                          <PermissionGate route={SUPPLIER_ROUTE} action="delete">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteSupplier(supplier)}
                              className="h-8 w-8 p-0 border-red-200 text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </PermissionGate>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center mt-6">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Statistics Cards */}
      {/* <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Suppliers</p>
                <p className="text-2xl font-semibold text-primary-700">{suppliers.length}</p>
              </div>
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center">
                <Building2 className="h-5 w-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Suppliers</p>
                <p className="text-2xl font-semibold text-green-700">
                  {suppliers.filter(s => s.isActive).length}
                </p>
              </div>
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center">
                <Building2 className="h-5 w-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Inactive Suppliers</p>
                <p className="text-2xl font-semibold text-red-700">
                  {suppliers.filter(s => !s.isActive).length}
                </p>
              </div>
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center">
                <Building2 className="h-5 w-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">This Month</p>
                <p className="text-2xl font-semibold text-blue-700">
                  {suppliers.filter(s => new Date(s.createdAt).getMonth() === new Date().getMonth()).length}
                </p>
              </div>
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                <Plus className="h-5 w-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div> */}
    </div>
  );
}